import { uuid } from '@rematter/ui-web';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Atom } from 'jotai';
import { atom, Getter, Setter } from 'jotai';

import { fileDeleteMutationAtom } from '../_graphql/mutationFileDelete';
import { invoiceCreateFromReconciliationAtom } from '../_graphql/mutationInvoiceCreateFromReconciliation';
import { invoiceUpdateLineItemsAtom } from '../_graphql/mutationInvoiceUpdateLineItems';
import { loadCreatePhotoMutationAtom } from '../_graphql/mutationLoadCreatePhoto';
import { loadMarkAsReceivedAtom } from '../_graphql/mutationLoadMarkAsReceived';
import {
  loadMarkAsShippedAtom,
  LoadMarkAsShippedParams,
} from '../_graphql/mutationLoadMarkAsShipped';
import { loadMarkAsVoidedAtom } from '../_graphql/mutationLoadMarkAsVoided';
import { loadUpdatePhotoMutationAtom } from '../_graphql/mutationLoadPhotoUpdate';
import { loadReconcileAtom } from '../_graphql/mutationLoadReconcile';
import { loadRevertStatusAtom } from '../_graphql/mutationLoadRevertStatus';
import {
  loadUpdateAtom,
  LoadUpdateParams,
} from '../_graphql/mutationLoadUpdate';
import {
  salesOrderUpdateAtom,
  SalesOrderUpdateParams,
} from '../_graphql/mutationSalesOrderUpdate';
import { bookingPhotoRequirementsQueryAtom } from '../_graphql/queryBookingPhotoRequirements';
import { loadDetailQueryAtom } from '../_graphql/queryLoadDetail';
import { loadPhotosQueryAtom } from '../_graphql/queryLoadPhotos';
import { generateFileAccessURLMutationAtom } from '../hooks/useFileDownloadUrl';
import { tradeRoutePaths } from '../routes';
import { LoadDetailsTab } from '../types/load';
import { LoadFormLineItem, LoadFormState } from '../types/load';
import { PhotoOptimisticState } from '../types/photo';
import { toMoney } from '../utils/money';

import { filesWithUrlsAtom, $optimisticPhotoUpdates } from './files.state';
import {
  $discardBaseline,
  $loadForm,
  $previousSalesOrderItemIds,
  $salesOrderLineItems,
  $selectedSalesOrderId,
  $updateLoadForm,
} from './loadForm.state';
import { $remoteLoads, $loadsCountingByStatus } from './loadsList.state';
import { orphanedLinkAtom } from './orphanedLinkState';

import { fileConfirmUploadAtom } from '~/modules/trade/_graphql/mutationFileConfirmUpload';
import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';
import { FileMetadata } from '~/shared/types/file';

// 🟣🟣🟣 PARAMS - Load Detail View
export const $loadDetailsPathParams = atomFromRoutePath(
  tradeRoutePaths.loadDetail,
);
export const $loadId = atom((get) =>
  get($loadDetailsPathParams)?.id
    ? String(get($loadDetailsPathParams)?.id)
    : undefined,
);

export const $isLoadCreateMode = atom((get) => {
  const id = get($loadId);
  return id === 'new';
});

// 🟢🟢🟢 REMOTE DATA - Load Detail
export const $loadDetail = loadDetailQueryAtom((get) => {
  const loadId = get($loadId);
  const isCreateMode = get($isLoadCreateMode);
  return {
    variables: { id: loadId ?? '' },
    skip: !loadId || isCreateMode,
  };
});

// 🟢🟢🟢 COMPUTED DATA - Booking ID
export const $bookingId = atom(
  (get) => get($loadDetail)?.data?.load?.bookingId,
);

// 🟢🟢🟢 REMOTE DATA - Booking Photo Requirements (Lazy)
export const $lazyBookingPhotoRequirements = bookingPhotoRequirementsQueryAtom(
  () => ({
    variables: {} as never,
    lazy: true,
  }),
);

// 🟠🟠🟠 ACTIONS - UPDATE Load
export const $updateLoad = loadUpdateAtom({
  onSuccess: (get, set, { result }) => {
    // Refetch load detail after successful update
    get($loadDetail).refetch();

    // Handle orphaned link if present
    if (result?.tradeLoadUpdate?.orphanedLink) {
      set(orphanedLinkAtom, result.tradeLoadUpdate.orphanedLink);
    }
  },
});

// 🟠🟠🟠 ACTIONS - UPDATE Sales Order Line Items
export const $updateSalesOrderLineItems = salesOrderUpdateAtom({
  onSuccess: (get, set) => {
    // Refetch sales order lineItems
    get($salesOrderLineItems).refetch();

    // Update form to set includeInSalesOrder: false for all line items
    const currentForm = get($loadForm);
    set($updateLoadForm, {
      lineItems: currentForm.lineItems.map((item) => ({
        ...item,
        includeInSalesOrder: false,
      })),
    });
  },
});

// 🟠🟠🟠 ACTIONS - UPDATE SALES ORDER AND LOAD
// If salesOrderParams provided: updates sales order first, then load
// Always refetches load detail and handles orphaned links via $updateLoad
export const $updateLoadWithSalesOrderUpdate = atom(
  null,
  async (
    get,
    set,
    params: {
      salesOrderParams?: SalesOrderUpdateParams;
      loadParams: LoadUpdateParams;
    },
  ) => {
    // Update sales order first if params provided
    if (params.salesOrderParams) {
      await set($updateSalesOrderLineItems, params.salesOrderParams);
    }

    // Always update load (which handles refetch and orphaned links)
    await set($updateLoad, params.loadParams);
  },
);

export type SyncAndSaveParams = {
  loadId: string;
  persistedSalesOrderId?: string | null;
  getSalesOrderParams: (
    syncedLineItems: LoadFormLineItem[],
  ) => SalesOrderUpdateParams | undefined;
  transformToLoadInput: (
    syncedLineItems: LoadFormLineItem[],
  ) => Record<string, unknown>;
};

const syncLineItemsForSave = async (
  get: Getter,
  set: Setter,
  form: LoadFormState,
  newSalesOrderId: string,
): Promise<{ lineItems: LoadFormLineItem[]; form: LoadFormState }> => {
  set($selectedSalesOrderId, newSalesOrderId);

  const salesOrderLineItemsQuery = get($salesOrderLineItems);
  await salesOrderLineItemsQuery.refetch();

  const refetchedQuery = get($salesOrderLineItems);
  const newSalesOrderLineItems =
    refetchedQuery.data?.salesOrders.entries[0]?.lineItems ?? [];

  const previousSalesOrderItemIds = get($previousSalesOrderItemIds);

  const filteredLineItems =
    previousSalesOrderItemIds.size > 0
      ? form.lineItems.filter(
          (item) => !item.itemId || !previousSalesOrderItemIds.has(item.itemId),
        )
      : form.lineItems;

  const salesOrderLineItemsByItemId = new Map(
    newSalesOrderLineItems.map((item) => [item.item.id, item]),
  );
  const existingItemIds = new Set(
    filteredLineItems
      .filter((item) => item.itemId)
      .map((item) => item.itemId as string),
  );

  const updatedLineItems = filteredLineItems.map((lineItem) => {
    if (!lineItem.itemId) return lineItem;
    const matchingSalesOrderLine = salesOrderLineItemsByItemId.get(
      lineItem.itemId,
    );
    if (!matchingSalesOrderLine) return lineItem;
    return {
      ...lineItem,
      includeInSalesOrder: false,
      unitPrice: toMoney(matchingSalesOrderLine.unitPrice),
      pricingUnit: matchingSalesOrderLine.unit,
    };
  });

  const newLineItems: LoadFormLineItem[] = newSalesOrderLineItems
    .filter((soLine) => !existingItemIds.has(soLine.item.id))
    .map((soLine) => ({
      type: 'Raw' as const,
      id: `so-${uuid()}`,
      itemId: soLine.item.id,
      tagId: null,
      unitPrice: toMoney(soLine.unitPrice),
      pricingUnit: soLine.unit,
      salesOrderLineId: soLine.id,
      includeInSalesOrder: false,
    }));

  const syncedLineItems = [...updatedLineItems, ...newLineItems];
  const updatedForm: LoadFormState = { ...form, lineItems: syncedLineItems };

  set($loadForm, updatedForm);
  set(
    $previousSalesOrderItemIds,
    new Set(newSalesOrderLineItems.map((item) => item.item.id)),
  );
  set($discardBaseline, updatedForm);

  return { lineItems: syncedLineItems, form: updatedForm };
};

// 🟠🟠🟠 ACTIONS - SYNC LINE ITEMS AND SAVE LOAD
// Syncs line items with new sales order before saving if salesOrderId changed
export const $syncAndSaveLoad = atom(
  null,
  async (get, set, params: SyncAndSaveParams) => {
    const {
      loadId,
      persistedSalesOrderId,
      getSalesOrderParams,
      transformToLoadInput,
    } = params;

    const initialForm = get($loadForm);
    const formSalesOrderId = initialForm.salesOrderId;

    const salesOrderChanged =
      formSalesOrderId !== persistedSalesOrderId &&
      formSalesOrderId !== undefined;

    const { lineItems, form } =
      salesOrderChanged && formSalesOrderId
        ? await syncLineItemsForSave(get, set, initialForm, formSalesOrderId)
        : { lineItems: initialForm.lineItems, form: initialForm };

    const loadInput = transformToLoadInput(lineItems);
    const salesOrderParams = getSalesOrderParams(lineItems);

    if (salesOrderParams) {
      await set($updateSalesOrderLineItems, salesOrderParams);
    }

    await set($updateLoad, { id: loadId, ...loadInput });

    return form;
  },
);

// 🟠🟠🟠 ACTIONS - MARK AS SHIPPED
export const $markAsShipped = loadMarkAsShippedAtom({
  onSuccess: (get) => {
    get($loadDetail).refetch();
  },
});

// 🟠🟠🟠 ACTIONS - MARK AS SHIPPED AND UPDATE LOAD
// Updates load first, then marks as shipped only if update succeeds
export const $markAsShippedAndUpdateLoad = atom(
  null,
  async (
    get,
    set,
    params: {
      markAsShipped: LoadMarkAsShippedParams;
      updateLoad: LoadUpdateParams;
    },
  ) => {
    const updateLoadAtom = loadUpdateAtom();
    await set(updateLoadAtom, params.updateLoad);

    const markAsShippedAtom = loadMarkAsShippedAtom();
    await set(markAsShippedAtom, params.markAsShipped);

    get($loadDetail).refetch();
  },
);

// 🟠🟠🟠 ACTIONS - MARK AS RECEIVED
export const $markAsReceived = loadMarkAsReceivedAtom({
  onSuccess: (get) => {
    get($loadDetail).refetch();
  },
});

// 🟠🟠🟠 ACTIONS - MARK AS RECONCILED
export const $markAsReconciled = loadReconcileAtom({
  onSuccess: (get) => {
    get($loadDetail).refetch();
    // Also refresh loads list and status counts
    get($remoteLoads).refetch?.();
    get($loadsCountingByStatus).refetch?.();
  },
});

// 🟠🟠🟠 ACTIONS - MARK AS VOIDED
export const $markAsVoided = loadMarkAsVoidedAtom({
  onSuccess: (get) => {
    get($loadDetail).refetch();
  },
});

// 🟠🟠🟠 ACTIONS - REVERT STATUS
export const $revertStatus = loadRevertStatusAtom({
  onSuccess: (get) => {
    get($loadDetail).refetch();
  },
});

// 🟠🟠🟠 ACTIONS - CREATE Invoice
export const $createInvoiceFromReconciliation =
  invoiceCreateFromReconciliationAtom({
    onSuccess: (get) => {
      // Refetch load detail to get updated invoice information
      get($loadDetail).refetch();
    },
  });

// 🟠🟠🟠 ACTIONS - UPDATE Invoice Line Items
export const $updateInvoiceLineItems = invoiceUpdateLineItemsAtom({
  onSuccess: (get) => {
    // Refetch load detail to get updated invoice information
    get($loadDetail).refetch();
  },
});

// Helper function to refetch load photos after mutations
const refetchLoadPhotos = (get: Getter, _set: Setter, _params: unknown) => {
  // Refetch underlying query
  get($lazyLoadPhotosWithoutUrl).refetch();
  // Also refetch the derived query with URLs to ensure UI updates
  get($loadPhotos).refetch();
};

// 🟠🟠🟠 ACTIONS - CREATE LOAD PHOTO
// Note: Don't refetch here - file record is created but not yet uploaded to S3
// We'll refetch after confirmLoadPhotoUpload when file is truly ready
export const $createLoadPhoto = loadCreatePhotoMutationAtom();

// 🟠🟠🟠 ACTIONS - CONFIRM LOAD PHOTO UPLOAD
// Note: No onSuccess refetch here - FilesTab will trigger refetch after all uploads complete
// to avoid race conditions with parallel uploads
export const $confirmLoadPhotoUpload = fileConfirmUploadAtom();

// 🟠🟠🟠 ACTIONS - UPDATE LOAD PHOTO
export const $updateLoadPhoto = loadUpdatePhotoMutationAtom({
  onSuccess: refetchLoadPhotos,
});

// 🟠🟠🟠 ACTIONS - DELETE LOAD PHOTO
export const $deleteLoadPhoto = fileDeleteMutationAtom({
  onSuccess: refetchLoadPhotos,
});

// 🟠🟠🟠 ACTIONS - GENERATE FILE ACCESS URL
export const $generateFileAccessURL = generateFileAccessURLMutationAtom();

// 🔘🔘🔘 PLAIN ATOMS - UI State
export const $tab = atomFromQueryString.string<LoadDetailsTab>('tab');
export const $isEditMode = atom<boolean>(false);
export const $hasUnsavedChanges = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA - Load Data
export const $loadData = atom((get) => {
  const data = get($loadDetail);
  return data?.data?.load ?? null;
});

export const $isLoadLoading = atom((get) => {
  const data = get($loadDetail);
  return data?.isLoading ?? false;
});

export const $loadError = atom((get) => {
  const data = get($loadDetail);
  return data?.error ?? null;
});

// 🟢🟢🟢 COMPUTED DATA - Photo Requirements
export const $photoRequirementsData = atom((get) => {
  const data = get($lazyBookingPhotoRequirements);
  return data?.data ?? [];
});

export const $photoRequirementsCount = atom((get) => {
  const requirements = get($photoRequirementsData);
  return requirements.length;
});

export const $uploadedPhotosCount = atom((get) => {
  const photosQuery = get($lazyLoadPhotosWithoutUrl);
  const photos = photosQuery?.data ?? [];
  const requirements = get($photoRequirementsData);
  if (photos.length === 0 || requirements.length === 0) return 0;

  // Count unique requirements that have at least one photo attached
  const satisfiedRequirementsCount = requirements.filter((req) =>
    photos.some((file) => file.metadata?.photoRequirementId === req.id),
  ).length;

  return satisfiedRequirementsCount;
});

export const $photoUploadStatus = atom((get) => {
  const requirements = get($photoRequirementsData);
  const photosQuery = get($lazyLoadPhotosWithoutUrl);
  const photos = photosQuery?.data ?? [];

  if (requirements.length === 0) return 'complete';

  const requiredPhotos = requirements.filter((req) => req.required);
  const optionalPhotos = requirements.filter((req) => !req.required);

  const uploadedRequiredCount = requiredPhotos.filter((req) =>
    photos.some((file) => file.metadata?.photoRequirementId === req.id),
  ).length;

  const uploadedOptionalCount = optionalPhotos.filter((req) =>
    photos.some((file) => file.metadata?.photoRequirementId === req.id),
  ).length;

  if (uploadedRequiredCount < requiredPhotos.length) {
    return 'incomplete'; // Missing required photos - red
  }

  if (uploadedOptionalCount < optionalPhotos.length) {
    return 'partial'; // Missing optional photos - yellow
  }

  return 'complete'; // All photos uploaded - green
});

export const $canShipLoad = atom((get) => {
  const status = get($photoUploadStatus);
  const load = get($loadData);

  // If no booking, can always ship
  if (!load?.bookingId) return true;

  // Can ship if not incomplete (i.e., all required photos are uploaded)
  return status !== 'incomplete';
});

// 🟢🟢🟢 REMOTE DATA -> load photos without url
export const $lazyLoadPhotosWithoutUrl = loadPhotosQueryAtom((get) => ({
  variables: { id: get($loadId) },
  lazy: true,
}));

// 🟢🟢🟢 COMPUTED DATA -> load photos with urls
export const $loadPhotos = filesWithUrlsAtom((get) => ({
  variables: { id: get($loadId) },
  skip: !get($loadId),
  photosAtom: $lazyLoadPhotosWithoutUrl,
}));

// Type for photo data with optimistic state info
export type PhotoWithOptimistic = {
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  status: string;
  metadata?: FileMetadata | null;
  createdAt: unknown;
  updatedAt: unknown;
  _optimistic?: PhotoOptimisticState;
};

// 🟢🟢🟢 COMPUTED DATA -> load photos with optimistic updates merged
export const $loadPhotosWithOptimistic = atom((get) => {
  const serverPhotosResult = get($loadPhotos);
  const optimisticUpdates = get($optimisticPhotoUpdates);

  const serverPhotos = serverPhotosResult.data;
  if (!serverPhotos) {
    return {
      ...serverPhotosResult,
      data: undefined as PhotoWithOptimistic[] | undefined,
    };
  }

  // Early return if no optimistic updates - still need to add _optimistic field for type consistency
  if (optimisticUpdates.size === 0) {
    return {
      ...serverPhotosResult,
      data: serverPhotos.map(
        (photo): PhotoWithOptimistic => ({
          ...photo,
          _optimistic: undefined,
        }),
      ),
    };
  }

  // Filter deleted photos and merge updates
  const mergedPhotos = serverPhotos
    .filter((photo) => {
      const update = optimisticUpdates.get(photo.id);
      // Only hide if delete is pending (not errored)
      return !(update?.type === 'delete' && update.status === 'pending');
    })
    .map((photo): PhotoWithOptimistic => {
      const update = optimisticUpdates.get(photo.id);
      if (update) {
        // Add optimistic state info to photo for UI rendering
        return {
          ...photo,
          ...(update.type === 'update' && update.status === 'pending'
            ? update.data
            : {}),
          _optimistic: {
            type: update.type,
            status: update.status,
            error: update.error,
          },
        };
      }
      return {
        ...photo,
        _optimistic: undefined,
      };
    });

  return {
    ...serverPhotosResult,
    data: mergedPhotos,
  };
}) as unknown as Atom<UseQueryResult<PhotoWithOptimistic[], Error>>;

// 🟠🟠🟠 ACTIONS -  CHECK LOAD PHOTO UPLOADS VALIDITY
export const $checkLoadPhotoUploadsValidity = atom(
  (get) => async (loadId: string, bookingId: string) => {
    // Check if photos data is already available to avoid over-fetching
    const photosQuery = get($lazyLoadPhotosWithoutUrl);
    const photos =
      photosQuery.data && photosQuery.isFetched
        ? photosQuery.data
        : await photosQuery.fetch({ id: loadId });

    // Check if requirements data is already available to avoid over-fetching
    const requirementsQuery = get($lazyBookingPhotoRequirements);
    const requirements =
      requirementsQuery.data && requirementsQuery.isFetched
        ? requirementsQuery.data
        : await requirementsQuery.fetch({ bookingId });

    const requiredIds = requirements
      .filter((req) => req.required)
      .map((req) => req.id);

    const uploadedIdsMap = new Map(
      photos
        .filter((photo) => photo.metadata?.photoRequirementId != null)
        .map((photo) => [photo.metadata?.photoRequirementId, photo.id]),
    );

    for (const requirementId of requiredIds) {
      if (!uploadedIdsMap.has(requirementId)) {
        return false;
      }
    }

    return true;
  },
);
